import React, { useMemo, useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Collapse,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  AccountBox as AccountBoxIcon,
  AccountBalance as AccountBalanceIcon,
  Inventory as InventoryIcon,
  Apartment as ApartmentIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Login as LoginIcon,
  AssignmentTurnedIn as CompletedIcon,
  Assignment as AssignmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  QuestionAnswer as QuestionAnswerIcon,
  UploadFile as UploadFileIcon,
  Launch as LaunchIcon,
  CommentBank as CommentBankIcon,
  ExpandLess,
  ExpandMore,
  SettingsApplications as SettingsApplicationsIcon,
  Payment as PaymentIcon
} from "@mui/icons-material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { Link, useLocation } from "react-router-dom";

const drawerWidth = 240;
const collapsedWidth = 70;

const externalLinks = [
  {
    label: "EC",
    icon: <LaunchIcon />,
    external: true,
    url: "https://tnreginet.gov.in/portal/",
  },
  {
    label: "Patta",
    icon: <LaunchIcon />,
    external: true,
    url: "https://eservices.tn.gov.in/eservicesnew/home.html",
  },
];

const settingsSubMenu = [
    { label: "Profile", path: "/settings/profile", icon: <AccountBoxIcon /> },
    { label: "Bank Branches", path: "/settings/bank-branches", icon: <AccountBalanceIcon /> },
    { label: "Bank Products", path: "/settings/bank-products", icon: <InventoryIcon /> },
    { label: "Yaazh Branches", path: "/settings/yaazh-branches", icon: <ApartmentIcon /> },
    { label: "Format Setup", path: "/settings/file-format-setup", icon: <SettingsApplicationsIcon /> },
    { label: "Format Master", path: "/settings/file-format-master", icon: <DescriptionIcon /> },
  ];

const adminMenu = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  { label: "Employees", icon: <PeopleIcon />, path: "/employees" },
  { label: "Expenses", icon: <PaymentIcon />, path: "/expenses" },
  { label: "Manage Files", icon: <FolderOpenIcon />, path: "/manage-files" },
  { label: "Login Files", icon: <LoginIcon />, path: "/login-files" },
  { label: "Assigned Files", icon: <AssignmentIcon />, path: "/assigned-files" },
  { label: "Qry-Raised Files", icon: <QuestionAnswerIcon />, path: "/query-raised-files" },
  { label: "Qry-Bank Files", icon: <CommentBankIcon />, path: "/query-raised-to-bank-files" },
  { label: "Completed Files", icon: <CompletedIcon />, path: "/completed-files" },
  { label: "Settings", icon: <SettingsIcon />, children: settingsSubMenu },
  ...externalLinks,
];

const branchAdminMenu = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  { label: "Expenses", icon: <PaymentIcon />, path: "/expenses" },
  { label: "Login Files", icon: <LoginIcon />, path: "/login-files" },
  { label: "Assigned Files", icon: <AssignmentIcon />, path: "/assigned-files" },
  { label: "Qry-Raised Files", icon: <QuestionAnswerIcon />, path: "/query-raised-files" },
  { label: "Qry-Bank Files", icon: <CommentBankIcon />, path: "/query-raised-to-bank-files" },
  { label: "Completed Files", icon: <CompletedIcon />, path: "/completed-files" },
  { label: "Settings", icon: <SettingsIcon />, path: "/processor-settings" },
  ...externalLinks,
  ];

const loginOfficerMenu = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  { label: "Expenses", icon: <PaymentIcon />, path: "/expenses" },
  { label: "Login Files", icon: <LoginIcon />, path: "/login-files" },
  { label: "Assigned Files", icon: <AssignmentIcon />, path: "/assigned-files" },
  { label: "Qry-Raised Files", icon: <QuestionAnswerIcon />, path: "/query-raised-files" },
  { label: "Qry-Bank Files", icon: <CommentBankIcon />, path: "/query-raised-to-bank-files" },
  { label: "Completed Files", icon: <CompletedIcon />, path: "/completed-files" },
  { label: "Settings", icon: <SettingsIcon />, path: "/processor-settings" },
  ...externalLinks,
];

const processorMenu = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/processor-dashboard" },
  { label: "Expenses", icon: <PaymentIcon />, path: "/expenses" },
  { label: "Assigned Files", icon: <AssignmentIcon />, path: "/processor-assigned-files" },
  { label: "Qry-Uploaded Files", icon: <UploadFileIcon />, path: "/processor-query-uploaded-files" },
  { label: "Completed Files", icon: <CompletedIcon />, path: "/processor-completed-files" },
  { label: "Settings", icon: <SettingsIcon />, path: "/processor-settings" },
    ...externalLinks,
  ];

const PvtProcessorMenu = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/processor-dashboard" },
  { label: "Expenses", icon: <PaymentIcon />, path: "/expenses" },
  { label: "Login Files", icon: <LoginIcon />, path: "/login-files" },
  { label: "Assigned Files", icon: <AssignmentIcon />, path: "/processor-assigned-files" },
  //{ label: "Qry-Uploaded Files", icon: <UploadFileIcon />, path: "/processor-query-uploaded-files" },
  { label: "Completed Files", icon: <CompletedIcon />, path: "/processor-completed-files" },
  { label: "Settings", icon: <SettingsIcon />, path: "/processor-settings" },
    ...externalLinks,
  ];

const MODExecutiveMenu = [
  { label: "Expenses", icon: <PaymentIcon />, path: "/expenses" },
  { label: "Settings", icon: <SettingsIcon />, path: "/processor-settings" },
    ...externalLinks,
  ];

function getMenuByRole(role) {
  switch (role) {
    case "Super Admin": return adminMenu;
    case "Branch Admin": return branchAdminMenu;
    case "Login Officer": return loginOfficerMenu;
    case "Processor": return processorMenu;
    case "Private Processor": return PvtProcessorMenu;
    case "MOD Executive": return MODExecutiveMenu;
    default: return [];
  }
}

export default function Sidebar({ mobileOpen, handleDrawerToggle }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const userRole = useMemo(() => JSON.parse(localStorage.getItem("user"))?.role, []);
  const [menuItems, setMenuItems] = useState([]);
  const [openSettings, setOpenSettings] = useState(false);

  useEffect(() => {
    setMenuItems(getMenuByRole(userRole));
  }, [userRole]);

  const drawerContent = (
    <Box
      sx={{
        backgroundColor: "#131d38",
        marginTop: "60px",
        height: "calc(100vh - 60px)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflowY: "auto",
      }}
    >
      {/* Mobile Close Button */}
      <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "flex-end", p: 1 }}>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        {menuItems.map(item => {
          const { label, icon, path, external, url, children } = item;

          if (children) {
            // Parent active if any child matches
            const isActive = children.some(child => location.pathname === child.path);

            return (
              <Box key={label}>
                <ListItem
                  button
                  onClick={() => setOpenSettings(!openSettings)}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    backgroundColor: isActive ? "#1e2a4a" : "transparent",
                    "&:hover": { backgroundColor: "#263859" },
                  }}
                >
                  <ListItemIcon sx={{ color: "#fff", minWidth: 0, justifyContent: "center", mr: collapsed ? 0 : 2 }}>
                    {icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{ fontWeight: isActive ? "bold" : "normal", color: "#fff" }}
                    />
                  )}
                  {!collapsed && (openSettings ? <ExpandLess sx={{ color: "#fff" }} /> : <ExpandMore sx={{ color: "#fff" }} />)}
                </ListItem>

                <Collapse in={openSettings} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {children.map(sub => {
                      const subActive = location.pathname === sub.path;
                      return (
                        <ListItem
                          key={sub.label}
                          component={Link}
                          to={sub.path}
                          onClick={handleDrawerToggle}
                          sx={{
                            pl: 4,
                            mb: 0.5,
                            borderRadius: 1,
                            backgroundColor: subActive ? "#1e2a4a" : "transparent",
                            "&:hover": { backgroundColor: "#263859" },
                          }}
                        >
                          <ListItemIcon sx={{ color: "#fff", minWidth: 0, justifyContent: "center", mr: 2 }}>
                            {sub.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={sub.label}
                            primaryTypographyProps={{ fontWeight: subActive ? "bold" : "normal", color: "#fff" }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          const active = !external && location.pathname === path;

          if (external) {
            return (
              <ListItem
                key={label}
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "#263859" },
                }}
              >
                <ListItemIcon sx={{ color: "#fff", minWidth: 0, justifyContent: "center", mr: collapsed ? 0 : 2 }}>
                  {icon}
                </ListItemIcon>
                {!collapsed && <ListItemText primary={label} primaryTypographyProps={{ color: "#fff" }} />}
              </ListItem>
            );
          }

          return (
            <ListItem
              key={label}
              component={Link}
              to={path}
              onClick={handleDrawerToggle}
              sx={{
                mx: 1,
                mb: 0.5,
                borderRadius: 1,
                backgroundColor: active ? "#1e2a4a" : "transparent",
                "&:hover": { backgroundColor: "#263859" },
              }}
            >
              <ListItemIcon sx={{ color: "#fff", minWidth: 0, justifyContent: "center", mr: collapsed ? 0 : 2 }}>
                {icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontWeight: active ? "bold" : "normal", color: "#fff" }}
                />
              )}
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ textAlign: "right", py: 1 }}>
        <IconButton onClick={() => setCollapsed(!collapsed)} sx={{ color: "#fff" }}>
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: drawerWidth },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          width: collapsed ? collapsedWidth : drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: collapsed ? collapsedWidth : drawerWidth,
            backgroundColor: "#131d38",
            color: "#fff",
            overflowX: "hidden",
            transition: "width 0.3s",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
